import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/App';
import { useI18n } from '@/i18n';
import { differenceInDays } from 'date-fns';
import { toast } from 'sonner';

function _localeCurrency(locale, fallback) {
  if (locale === 'pt') return 'BRL';
  if (locale === 'en') return 'USD';
  return fallback;
}

export function usePropertyDetail() {
  const { id: propertyId } = useParams();
  const navigate = useNavigate();
  const { user, login, isAuthenticated, isAdmin } = useAuth();
  const { t, formatPrice, locale, currency } = useI18n();

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDates, setSelectedDates] = useState({ from: undefined, to: undefined });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [guestCounts, setGuestCounts] = useState({ adults: 1, children: 0, infants: 0 });
  const [guestPhone, setGuestPhone] = useState('');

  const [reviews, setReviews] = useState([]);
  const [reviewAverages, setReviewAverages] = useState(null);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    overall_rating: 5,
    categories: { cleanliness: 5, location: 5, service: 5, value: 5, amenities: 5 },
    comment: '', photos: []
  });
  const [reviewPhotoUploading, setReviewPhotoUploading] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favCount, setFavCount] = useState(0);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [bookingRequestStatus, setBookingRequestStatus] = useState(null);
  const [bookingRequestData, setBookingRequestData] = useState(null);
  const [approvedRequest, setApprovedRequest] = useState(null);
  const [payingApproved, setPayingApproved] = useState(false);
  const [depositPct, setDepositPct] = useState(1.0);

  const [activeRequest, setActiveRequest] = useState(null);

  // --- Data fetchers ---
  const fetchReviews = useCallback(async () => {
    try {
      const response = await api.get(`/properties/${propertyId}/reviews`);
      setReviews(response.data.reviews || []);
      setReviewAverages(response.data.averages || null);
      setReviewTotal(response.data.total || 0);
    } catch (_) { /* silent */ }
  }, [propertyId]);

  const fetchApprovedRequest = useCallback(async () => {
    try {
      const response = await api.get(`/booking-requests/by-property/${propertyId}`);
      const requests = response.data.requests || [];
      // Find active request: approved, pending_verification, paying, paid, or rejected
      const active = requests.find(r => ['approved', 'pending_verification', 'paying', 'paid', 'rejected'].includes(r.status));
      setActiveRequest(active || null);
      const approved = requests.find(r => r.status === 'approved');
      setApprovedRequest(approved || null);
    } catch (_) { setApprovedRequest(null); setActiveRequest(null); }
  }, [propertyId]);

  const checkFavorite = useCallback(async () => {
    try {
      const response = await api.get(`/favorites/check/${propertyId}`);
      setIsFavorite(response.data.is_favorite);
    } catch (_) { /* silent - user may not be authenticated */ }
  }, [propertyId]);

  const fetchFavCount = useCallback(async () => {
    try {
      const response = await api.get(`/favorites/count/${propertyId}`);
      setFavCount(response.data.count || 0);
    } catch (_) { /* silent */ }
  }, [propertyId]);

  const fetchPaymentInfo = useCallback(async () => {
    try {
      const response = await api.get('/settings/payment-info');
      setPaymentInfo(response.data);
    } catch (_) { /* silent */ }
  }, []);

  const fetchDepositPct = useCallback(async () => {
    try {
      const response = await api.get('/settings/deposit-percentage');
      if (response.data?.deposit_percentage != null) setDepositPct(response.data.deposit_percentage);
    } catch (_) { /* optional */ }
  }, []);

  const fetchBlockedDates = useCallback(async () => {
    try {
      const response = await api.get(`/properties/${propertyId}/calendar`);
      setBlockedDates(response.data.blocked_dates || []);
    } catch (_) { /* silent */ }
  }, [propertyId]);

  const fetchProperty = useCallback(async () => {
    try {
      const response = await api.get(`/properties/${propertyId}`);
      setProperty(response.data);
    } catch (error) {
      navigate('/properties');
    } finally { setLoading(false); }
  }, [propertyId, navigate]);

  // --- Effects ---
  useEffect(() => {
    fetchProperty();
    fetchBlockedDates();
    fetchFavCount();
    fetchPaymentInfo();
    fetchDepositPct();
  }, [fetchProperty, fetchBlockedDates, fetchFavCount, fetchPaymentInfo, fetchDepositPct]);

  useEffect(() => {
    if (propertyId) fetchReviews();
  }, [propertyId, fetchReviews]);

  useEffect(() => {
    if (isAuthenticated && propertyId) {
      checkFavorite();
      fetchApprovedRequest();
    }
  }, [isAuthenticated, propertyId, checkFavorite, fetchApprovedRequest]);

  // --- Actions ---
  const handlePayMercadoPago = async () => {
    if (!approvedRequest) return;
    setPayingApproved(true);
    try {
      const res = await api.post(`/booking-requests/${approvedRequest.request_id}/checkout-mp`);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('No se pudo obtener la URL de pago de MercadoPago');
        setPayingApproved(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al procesar el pago con MercadoPago');
      setPayingApproved(false);
    }
  };

  const handlePayBancard = async () => {
    if (!approvedRequest) return;
    setPayingApproved(true);
    try {
      const res = await api.post(`/booking-requests/${approvedRequest.request_id}/checkout-bancard`);
      const { process_id, bancard_env, amount, request_id } = res.data;
      if (process_id) {
        // Redirect to our Bancard checkout page with process_id (new flow)
        window.location.href = `/pago-bancard?process_id=${process_id}&env=${bancard_env}&amount=${amount}&request_id=${request_id || approvedRequest.request_id}`;
      } else {
        toast.error('No se pudo obtener la URL de pago de Bancard');
        setPayingApproved(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al procesar el pago con Bancard');
      setPayingApproved(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) { toast.info('Inicia sesión para guardar favoritos'); return; }
    try {
      const res = await api.post(`/favorites/${propertyId}`);
      setIsFavorite(res.data.action === 'added');
      setFavCount(prev => res.data.action === 'added' ? prev + 1 : Math.max(0, prev - 1));
      toast.success(res.data.action === 'added' ? 'Agregado a favoritos' : 'Eliminado de favoritos');
    } catch (error) { toast.error('Error al actualizar favoritos'); }
  };

  const calculateNights = () => selectedDates.from && selectedDates.to ? differenceInDays(selectedDates.to, selectedDates.from) : 0;

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    const n = calculateNights();
    if (n <= 0) { toast.error('Selecciona fechas primero'); return; }
    setValidatingCoupon(true);
    try {
      const res = await api.post('/coupons/validate', { code: couponCode, property_id: propertyId, nights: n });
      setAppliedCoupon(res.data);
      toast.success(`Cupón ${res.data.code} aplicado: -$${res.data.discount_amount}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cupón no válido');
      setAppliedCoupon(null);
    } finally { setValidatingCoupon(false); }
  };

  const isDateBlocked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return blockedDates.some(bd => {
      const start = bd.start?.split('T')[0];
      const end = bd.end?.split('T')[0];
      return start && end && dateStr >= start && dateStr < end;
    });
  };

  const submitReview = async () => {
    if (!reviewForm.comment.trim() || reviewForm.comment.length < 10) {
      toast.error('El comentario debe tener al menos 10 caracteres'); return;
    }
    setSubmittingReview(true);
    try {
      await api.post(`/properties/${propertyId}/reviews`, {
        property_id: propertyId, overall_rating: reviewForm.overall_rating,
        categories: reviewForm.categories, comment: reviewForm.comment, photos: reviewForm.photos
      });
      toast.success('Reseña publicada');
      setShowReviewForm(false);
      setReviewForm({ overall_rating: 5, categories: { cleanliness: 5, location: 5, service: 5, value: 5, amenities: 5 }, comment: '', photos: [] });
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al publicar reseña');
    } finally { setSubmittingReview(false); }
  };

  const submitAdminResponse = async (reviewId, response) => {
    if (!response.trim()) return;
    try {
      await api.post(`/admin/reviews/${reviewId}/respond`, { response });
      toast.success('Respuesta publicada');
      fetchReviews();
    } catch (error) { toast.error('Error al responder'); }
  };

  const handleReviewPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setReviewPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setReviewForm(prev => ({ ...prev, photos: [...prev.photos, `/api/images/${res.data.image_id}`] }));
      toast.success('Foto agregada');
    } catch (error) { toast.error('Error al subir la foto'); }
    finally { setReviewPhotoUploading(false); e.target.value = ''; }
  };

  const calculateTotal = () => { const n = calculateNights(); return n > 0 && property?.price_min_usd ? (n * property.price_min_usd).toFixed(2) : 0; };
  const calculateDeposit = () => (parseFloat(calculateTotal()) * depositPct).toFixed(2);
  const depositLabel = `${Math.round(depositPct * 100)}%`;
  const maxGuests = property?.max_guests || property?.guests || 10;

  const updateGuestCount = (type, delta) => {
    setGuestCounts(prev => {
      const newVal = Math.max(type === 'adults' ? 1 : 0, prev[type] + delta);
      const totalGuests = (type === 'adults' ? newVal : prev.adults) + (type === 'children' ? newVal : prev.children);
      if (totalGuests > maxGuests) return prev;
      return { ...prev, [type]: newVal };
    });
  };

  useEffect(() => { if (user?.phone && !guestPhone) setGuestPhone(user.phone); }, [user?.phone]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePayment = async () => {
    if (!isAuthenticated) { toast.info('Inicia sesión para realizar la reserva'); login(); return; }
    const n = calculateNights();
    if (n <= 0) { toast.error('Selecciona las fechas de tu estadia'); return; }
    if (guestPhone.replace(/\D/g, '').length < 7) { toast.error('Ingresá tu teléfono de contacto (con código de país)'); return; }
    setBookingRequestStatus('sending');
    try {
      const response = await api.post('/booking-requests', {
        property_id: propertyId,
        check_in: selectedDates.from?.toISOString().split('T')[0] || '',
        check_out: selectedDates.to?.toISOString().split('T')[0] || '',
        nights: n, adults: guestCounts.adults, children: guestCounts.children, infants: guestCounts.infants,
        coupon_code: appliedCoupon?.code || null,
        currency: _localeCurrency(locale, currency),
        phone: guestPhone,
      });
      setBookingRequestData({ ...response.data, status: response.data.status || 'pending_verification' });
      setActiveRequest({ ...response.data, status: response.data.status || 'pending_verification' });
      setBookingRequestStatus('pending');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar la solicitud. Intenta nuevamente.');
      setBookingRequestStatus(null);
    }
  };

  return {
    propertyId, property, loading,
    selectedDates, setSelectedDates,
    processingPayment, bookingRequestStatus, bookingRequestData,
    couponCode, setCouponCode, appliedCoupon, validatingCoupon,
    guestCounts, maxGuests, guestPhone, setGuestPhone,
    reviews, reviewAverages, reviewTotal,
    showReviewForm, setShowReviewForm,
    submittingReview, reviewForm, setReviewForm,
    reviewPhotoUploading, blockedDates,
    isFavorite, favCount, paymentInfo,
    approvedRequest, payingApproved, activeRequest,
    // Computed
    nights: calculateNights(),
    total: calculateTotal(),
    deposit: calculateDeposit(),
    depositLabel,
    // Actions
    handlePayMercadoPago, handlePayBancard, copyToClipboard, toggleFavorite,
    validateCoupon, isDateBlocked, submitReview,
    submitAdminResponse, handleReviewPhotoUpload,
    updateGuestCount, handlePayment, setBookingRequestStatus,
    isAuthenticated, isAdmin, login, navigate, t,
  };
}
